'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  organizationType: string;
  organizationName: string;
  contactName: string;
  email: string;
  phone: string;
  country: string;
  partnershipInterest: string;
  investmentRange: string;
  timeline: string;
  message: string;
}

interface InquiryFormProps {
  className?: string;
}

const InquiryForm = ({ className = '' }: InquiryFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    organizationType: '',
    organizationName: '',
    contactName: '',
    email: '',
    phone: '',
    country: '',
    partnershipInterest: '',
    investmentRange: '',
    timeline: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const organizationTypes = [
    'Development Finance Institution',
    'Impact Investment Fund',
    'Pension Fund',
    'Government Agency',
    'Multinational Corporation',
    'NGO/Foundation',
    'Research Institution',
    'Media Organization',
    'Other'
  ];

  const partnershipInterests = [
    'Direct Investment',
    'Co-Investment Opportunity',
    'Public-Private Partnership',
    'Technical Collaboration',
    'Research Partnership',
    'Strategic Alliance',
    'Government Engagement',
    'Media Coverage'
  ];

  const investmentRanges = [
    'Under $10M',
    '$10M - $50M',
    '$50M - $100M',
    '$100M - $250M',
    'Over $250M',
    'Not Applicable'
  ];

  const timelines = [
    'Immediate (0-3 months)',
    'Short-term (3-6 months)',
    'Medium-term (6-12 months)',
    'Long-term (12+ months)',
    'Exploratory'
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitStatus('success');

    setTimeout(() => {
      setSubmitStatus('idle');
      setFormData({
        organizationType: '',
        organizationName: '',
        contactName: '',
        email: '',
        phone: '',
        country: '',
        partnershipInterest: '',
        investmentRange: '',
        timeline: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <section className={`py-16 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-headline text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Partnership Inquiry
            </h2>
            <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
              Submit your partnership inquiry and our team will respond within 48 hours with next steps.
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="organizationType" className="block font-body text-sm font-medium text-foreground mb-2">
                    Organization Type *
                  </label>
                  <select
                    id="organizationType"
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                  >
                    <option value="">Select organization type</option>
                    {organizationTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="organizationName" className="block font-body text-sm font-medium text-foreground mb-2">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    id="organizationName"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                    placeholder="Enter organization name"
                  />
                </div>

                <div>
                  <label htmlFor="contactName" className="block font-body text-sm font-medium text-foreground mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block font-body text-sm font-medium text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                    placeholder="your.email@organization.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block font-body text-sm font-medium text-foreground mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block font-body text-sm font-medium text-foreground mb-2">
                    Country *
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                    placeholder="Enter country"
                  />
                </div>

                <div>
                  <label htmlFor="partnershipInterest" className="block font-body text-sm font-medium text-foreground mb-2">
                    Partnership Interest *
                  </label>
                  <select
                    id="partnershipInterest"
                    name="partnershipInterest"
                    value={formData.partnershipInterest}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                  >
                    <option value="">Select partnership type</option>
                    {partnershipInterests.map((interest) => (
                      <option key={interest} value={interest}>{interest}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="investmentRange" className="block font-body text-sm font-medium text-foreground mb-2">
                    Investment Range
                  </label>
                  <select
                    id="investmentRange"
                    name="investmentRange"
                    value={formData.investmentRange}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                  >
                    <option value="">Select investment range</option>
                    {investmentRanges.map((range) => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="timeline" className="block font-body text-sm font-medium text-foreground mb-2">
                    Engagement Timeline *
                  </label>
                  <select
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
                  >
                    <option value="">Select timeline</option>
                    {timelines.map((timeline) => (
                      <option key={timeline} value={timeline}>{timeline}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="message" className="block font-body text-sm font-medium text-foreground mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-background border border-input rounded-md font-body text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300 resize-none"
                    placeholder="Please provide details about your partnership interest, specific objectives, and any questions you may have..."
                  />
                </div>
              </div>

              <div className="flex items-start space-x-3 pt-4">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  className="mt-1 w-4 h-4 text-primary border-input rounded focus:ring-2 focus:ring-ring"
                />
                <label htmlFor="terms" className="font-body text-sm text-text-secondary">
                  I agree to the terms and conditions and understand that my information will be processed according to Grandville Holdings&apos; privacy policy. I consent to being contacted regarding partnership opportunities.
                </label>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-body text-sm font-semibold text-success mb-1">
                      Inquiry Submitted Successfully
                    </h4>
                    <p className="font-body text-sm text-success/80">
                      Thank you for your interest. Our partnership team will review your inquiry and respond within 48 hours.
                    </p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-secondary text-secondary-foreground font-body font-medium text-base rounded-md hover:bg-secondary/90 hover:shadow-institutional transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Partnership Inquiry</span>
                    <Icon name="PaperAirplaneIcon" size={20} variant="outline" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InquiryForm;